import React, { useState, useEffect, useRef } from "react";
import EmployeeService from "../../../../services/EmployeeService";
import DropDownService from "../../../../services/DropDownService";

const MergePatientSelector = ({ type, onSelect, selectedPatient, excludeEmployeeId, disabled }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [companyNameMap, setCompanyNameMap] = useState({});
  const [clinicNameMap, setClinicNameMap] = useState({});
  const searchTimeoutRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const fetchCompanyAndClinicNames = async () => {
      try {
        const [companiesRes, clinicsRes] = await Promise.all([
          DropDownService.getCompanies(null),
          DropDownService.getClinicList(null),
        ]);
        const cMap = {};
        if (Array.isArray(companiesRes)) {
          companiesRes.forEach((c) => {
            if (c.name != null) {
              if (c.company_uuid != null) cMap[c.company_uuid] = c.name;
              if (c.company_id != null) cMap[String(c.company_id)] = c.name;
              if (c.id != null) cMap[String(c.id)] = c.name;
            }
          });
        }
        const clMap = {};
        if (Array.isArray(clinicsRes)) {
          clinicsRes.forEach((cl) => {
            if (cl.name != null && cl.clinic_id != null) {
              clMap[String(cl.clinic_id)] = cl.name;
            }
          });
        }
        setCompanyNameMap(cMap);
        setClinicNameMap(clMap);
      } catch (e) {
        // non-blocking
      }
    };
    fetchCompanyAndClinicNames();
  }, []);

  useEffect(() => {
    // Close results when clicking outside
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Allow searching with 1 character (per end-user request)
    if (searchQuery.length < 1) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch();
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async () => {
    setIsSearching(true);
    setShowResults(true);

    try {
      const filters = {
        search_query: searchQuery,
        perPage: 10,
        page: 1,
      };

      const response = await EmployeeService.getEmployeeList(filters);
      
      // Response structure: response.data.data contains the array of employees
      if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        let results = response.data.data;
        
        // Filter out excluded employee
        if (excludeEmployeeId) {
          results = results.filter(emp => emp.employee_id !== excludeEmployeeId);
        }

        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching patients:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePatientSelect = (patient) => {
    onSelect(patient);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  const handleClearSelection = () => {
    onSelect(null);
    setSearchQuery("");
    setSearchResults([]);
  };

  const formatPatientDisplay = (patient) => {
    return `${patient.first_name || ''} ${patient.last_name || ''} (DOB: ${patient.dob || 'N/A'})`;
  };

  const getCompanyName = (patient) => {
    if (!patient) return null;
    if (patient.company && typeof patient.company === 'object' && patient.company.name) return patient.company.name;
    if (patient.company && typeof patient.company === 'string' && isNaN(Number(patient.company))) return patient.company;
    const id = patient.company_id ?? patient.company_uuid ?? (patient.company != null ? String(patient.company) : null);
    if (id != null && companyNameMap[String(id)]) return companyNameMap[String(id)];
    return null;
  };
  const getClinicName = (patient) => {
    if (!patient) return null;
    if (patient.clinic && typeof patient.clinic === 'object' && patient.clinic.name) return patient.clinic.name;
    if (patient.clinic && typeof patient.clinic === 'string' && isNaN(Number(patient.clinic))) return patient.clinic;
    const id = patient.clinic_id ?? (patient.clinic != null ? String(patient.clinic) : null);
    if (id != null && clinicNameMap[String(id)]) return clinicNameMap[String(id)];
    return null;
  };

  const cardClass = type === 'primary'
    ? "merge-refined-card merge-refined-card-primary sh_cardBox border-0"
    : "merge-refined-card merge-refined-card-secondary sh_cardBox border-0";
  const cardTitle = type === 'primary' ? "Primary Patient (Keep)" : "Secondary Patient (Merge From)";
  const cardMeta = type === 'primary'
    ? "The chart you want to keep active."
    : "Data will merge into primary; this record will be deleted.";

  return (
    <div className={cardClass}>
      <div className="sh_cardHeader merge-refined-card-head">
        <h6 className="mb-0">{cardTitle}</h6>
        <span className="merge-refined-meta d-block mt-1">{cardMeta}</span>
        {disabled && (
          <span className="merge-refined-meta d-block mt-1 small text-muted">Selection locked. Use Back to change.</span>
        )}
      </div>
      <div className="sh_cardBody">
        <div className="form-group mb-0">
          <label className="form-label">Search by name</label>
          <div className="merge-search-wrap position-relative">
            <input
              type="text"
              className={`form-control form-control-solid ${isSearching ? 'pe-5' : ''} ${disabled ? 'bg-light' : ''}`}
              placeholder="Search employee by name..."
              value={searchQuery}
              onChange={(e) => !disabled && setSearchQuery(e.target.value)}
              onFocus={() => {
                if (!disabled && searchResults.length > 0) {
                  setShowResults(true);
                }
              }}
              disabled={disabled}
            />
            {isSearching && (
              <div className="position-absolute top-50 end-0 translate-middle-y me-3" style={{ pointerEvents: 'none' }}>
                <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading"></span>
              </div>
            )}
            {showResults && isSearching && (
              <div ref={resultsRef} className="merge-search-results list-group position-absolute w-100 p-3 d-flex justify-content-center align-items-center min-h-60">
                <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>
                <span className="text-muted">Loading...</span>
              </div>
            )}
            {!disabled && showResults && !isSearching && searchResults.length > 0 && (
              <div
                ref={resultsRef}
                className="merge-search-results list-group position-absolute w-100"
              >
                {searchResults.map((patient) => (
                  <button
                    key={patient.employee_id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => handlePatientSelect(patient)}
                    style={{ cursor: "pointer" }}
                  >
                    <div>
                      <strong>{patient.first_name} {patient.last_name}</strong>
                      <br />
                      <small className="text-muted">
                        DOB: {patient.dob || 'N/A'} | Phone: {patient.phone || 'N/A'}
                      </small>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!disabled && showResults && !isSearching && searchResults.length === 0 && searchQuery.length >= 1 && (
              <div ref={resultsRef} className="merge-search-results list-group position-absolute w-100 p-2">
                <p className="mb-0 text-muted">No patients found</p>
              </div>
            )}
          </div>
        </div>
        {/* Selected Patient Display */}
        {selectedPatient && (
          <div className="mt-2 merge-refined-card-body border rounded p-2" style={{ backgroundColor: "#f8f9fc" }}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="merge-refined-name">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </div>
                <div className="merge-refined-meta">
                  <span className="merge-refined-label">DOB:</span> {selectedPatient.dob || 'N/A'}
                </div>
                <div className="merge-refined-meta">
                  <span className="merge-refined-label">Phone:</span> {selectedPatient.phone || 'N/A'}
                </div>
              </div>
              {!disabled && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearSelection}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergePatientSelector;

